import { type CSSProperties, type RefObject, useCallback, useEffect, useRef, useState } from "react";

export interface UseClampedTextOptions {
  /** Number of text lines visible while collapsed. */
  lines: number;
}

export interface UseClampedTextResult {
  /** Collapse back to the clamped state. */
  collapse: () => void;
  /** Reveal the full text. */
  expand: () => void;
  expanded: boolean;
  /** Whether the collapsed text overflows the clamp (i.e. a toggle is needed). */
  isClamped: boolean;
  /** Attach to the collapsed, overflow-hidden container. */
  ref: RefObject<HTMLDivElement | null>;
  /** Apply to the collapsed container alongside `ref` — sets the line clamp. */
  style: CSSProperties;
}

/**
 * Clamp-to-N-lines measurement for expandable text: tracks whether the
 * collapsed container's content overflows its `maxHeight` (in `lh` units),
 * re-measuring on window resize and once web fonts load (font metrics shift
 * line wrapping), plus the expanded/collapsed toggle state.
 */
export function useClampedText({ lines }: UseClampedTextOptions): UseClampedTextResult {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsClamped(el.scrollHeight > el.clientHeight);
  }, []);

  useEffect(() => {
    if (expanded) return;
    let cancelled = false;
    measure();
    document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });
    window.addEventListener("resize", measure);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
    };
  }, [expanded, measure]);

  const expand = useCallback(() => setExpanded(true), []);
  const collapse = useCallback(() => setExpanded(false), []);

  return { collapse, expand, expanded, isClamped, ref, style: { maxHeight: `${lines}lh` } };
}
